import {
  createTRPCProxyClient,
  httpBatchLink,
  httpLink,
  splitLink,
} from '@trpc/client';
import { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies';
import superjson from 'superjson';
import {
  BULK_PROCEDURE_PATHS,
  BULK_TRPC_PREFIX,
} from 'backend/schema/bulk_procedure';

import { env } from './env';

import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from 'backend';

export const createApiHttpLink = (
  url: string,
  cookie?: string,
  proxy?: boolean,
) => {
  if (proxy) {
    url = '/api/trpc-proxy';
  }

  const options = {
    url,
    transformer: superjson,
    fetch(url: RequestInfo | URL, options?: RequestInit) {
      return fetch(url, {
        ...options,
        credentials: 'include',
      });
    },
    headers: { cookie },
  };

  const bulkProcedurePaths = new Set(BULK_PROCEDURE_PATHS);

  return splitLink({
    // 学習コンテンツの一括取り込みで、パートファイルを本文で送る手続きは、本文の上限を上げた口 (/trpc/bulk) に送る。
    // 既定の口は本文が 1MB までのため。束ねると、ほかの問い合わせまで大きな本文の口に送ることになるので束ねない
    condition: (op) => bulkProcedurePaths.has(op.path),
    true: httpLink({ ...options, url: `${url}/${BULK_TRPC_PREFIX}` }),
    // 受講履歴のレポート取得だけは束ねずに個別のリクエストにする。
    // httpBatchLink は束の中で最も遅い問い合わせが終わるまで応答を返さないため、
    // 重いレポートがあると、同じ画面のサマリーや他の表まで表示が待たされる。
    false: splitLink({
      condition: (op) => op.path === 'manager.analytics.getReport',
      true: httpLink(options),
      false: httpBatchLink(options),
    }),
  });
};

/**
 * サーバー側から呼び出すためのTRPCクライアント
 * クライアント側での使用は trpc.useUtils() を使用する
 *
 * @example
 * import { cookies } from 'next/headers';
 * const cookieStore = cookies();
 * const userInfo = await createTRPCClient(cookieStore).user.userInfo.query();
 *
 * @param cookieStore - Next.js の cookies() で取得したクッキー情報
 */
export const createTRPCClient = (cookieStore?: ReadonlyRequestCookies) => {
  const cookieRaw = cookieStore?.toString() ?? undefined;

  const cli = createTRPCProxyClient<AppRouter>({
    links: [createApiHttpLink(env['API_BASE_URL'], cookieRaw, false)],
  });

  return cli;
};

type RouterInput = inferRouterInputs<AppRouter>;
type RouterOutput = inferRouterOutputs<AppRouter>;

export type ManagerInput = RouterInput['manager'];
export type ManagerOutput = RouterOutput['manager'];

export type FranchiseManagerInput = RouterInput['franchiseManager'];
export type FranchiseManagerOutput = RouterOutput['franchiseManager'];

// エラーハンドリング用
// export function isTRPCClientError(
//   cause: unknown,
// ): cause is TRPCClientError<AppRouter> {
//   return cause instanceof TRPCClientError;
// }
